// src/components/bot/components/KeywordSection.tsx
// 🎨 KEYWORD SECTION - Keyword management UI
// ✅ FIXED: Event object issue resolved

import React from 'react';
import styles from '../BotForm.module.css';
import logger from '../../../utils/logger';

interface KeywordSectionProps {
  keywordInput: string;
  keywords: string[];
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  addKeyword: () => boolean;
  removeKeyword: (keyword: string) => void;
  canAddFromInput: () => boolean;
  keywordStats: {
    total: number;
    averageLength: number;
    longest: number;
    shortest: number;
  };
}

const KeywordSection: React.FC<KeywordSectionProps> = ({
  keywordInput,
  keywords,
  handleInputChange,
  handleKeyPress,
  addKeyword,
  removeKeyword,
  canAddFromInput,
  keywordStats
}) => {
  logger.log('🔑 KeywordSection: Rendered with', keywordStats.total, 'keywords');

  // ✅ FIXED: Proper click handler that doesn't pass event object
  const handleAddClick = () => {
    logger.log('🔘 KeywordSection: Add button clicked');
    addKeyword(); // Call without parameters - no event object passed
  };

  return (
    <div className={styles.sectionWide}>
      <h3>Trigger Keywords</h3>
      
      {/* 🎯 KEYWORD INPUT */}
      <div className={styles.field}>
        <label className={styles.label}>Add Keywords</label>
        <div className={styles.keywordInputContainer}>
          <input
            type="text"
            value={keywordInput}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder='Type keyword and press Enter (e.g., "hello", "info", or "any" for fallback)'
            className={styles.input}
          />
          {/* ✅ FIXED: Use proper click handler */}
          <button
            type="button"
            onClick={handleAddClick}
            disabled={!canAddFromInput()}
            className={styles.addKeywordBtn}
          >
            Add
          </button>
        </div>

        {/* 🎯 PREVIEW: Show when "any" is being typed */}
        {keywordInput.toLowerCase().trim() === 'any' && (
          <div style={{
            background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
            border: '1px solid #3b82f6',
            borderRadius: '8px',
            padding: '12px',
            marginTop: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <i className="fas fa-info-circle" style={{ color: '#3b82f6' }}></i>
            <span style={{ color: '#1e40af', fontSize: '13px', fontWeight: '500' }}>
              This will create a <strong>fallback bot</strong> that triggers when no other keywords match.
            </span>
          </div>
        )}
        <small className={styles.helpText}>
          Add words that will trigger this bot when users message you. 
          Press Enter or comma to add multiple keywords quickly.
          <br />
          <strong>Special:</strong> Use "any" keyword to create a fallback bot that triggers when no other bots match.
        </small>
      </div>

      {/* 🎯 KEYWORDS LIST */}
      {keywords.length > 0 && (
        <div className={styles.field}>
          <label className={styles.label}>
            Current Keywords ({keywordStats.total})
          </label>
          <div className={styles.keywordChips}>
            {keywords.map((keyword, index) => {
              const isAnyKeyword = keyword.toLowerCase() === 'any';
              return (
                <div 
                  key={index} 
                  className={styles.keywordChip}
                  style={isAnyKeyword ? {
                    background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                    border: '2px solid #d97706',
                    color: 'white',
                    fontWeight: 'bold'
                  } : {}}
                >
                  {isAnyKeyword && (
                    <i className="fas fa-star" style={{ marginRight: '6px', fontSize: '12px' }}></i>
                  )}
                  <span className={styles.keywordText} style={isAnyKeyword ? { color: 'white' } : {}}>
                    {/* ✅ SAFE: Render keyword as string, not object */}
                    {typeof keyword === 'string' ? keyword : 'Invalid keyword'}
                  </span>
                  {isAnyKeyword && (
                    <span style={{ 
                      fontSize: '10px', 
                      marginLeft: '6px', 
                      opacity: 0.9,
                      background: 'rgba(255,255,255,0.2)',
                      padding: '2px 6px',
                      borderRadius: '4px'
                    }}>
                      FALLBACK
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => removeKeyword(keyword)}
                    className={styles.removeKeywordBtn}
                    title={`Remove "${keyword}"`}
                    style={isAnyKeyword ? { color: 'white' } : {}}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 🎯 SPECIAL "ANY" KEYWORD WARNING */}
      {keywords.some(k => k.toLowerCase() === 'any') && (
        <div className={styles.field}>
          <div style={{
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            border: '2px solid #f59e0b',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <i className="fas fa-exclamation-triangle" style={{ color: '#f59e0b', fontSize: '20px', marginTop: '2px' }}></i>
              <div>
                <h4 style={{ margin: '0 0 8px 0', color: '#92400e', fontFamily: 'Gilroy, sans-serif', fontWeight: '700' }}>
                  "Any" Keyword Active - Fallback Bot
                </h4>
                <p style={{ margin: '0', color: '#92400e', fontSize: '14px', lineHeight: '1.5' }}>
                  This bot will trigger when <strong>no other bots match</strong> the customer's message. 
                  Only <strong>one bot can use "any"</strong> keyword. Use this for general inquiries and fallback responses.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 🎯 KEYWORD TIPS */}
      <div className={styles.keywordTips}>
        <div className={styles.tipsHeader}>
          <i className="fas fa-lightbulb" style={{ color: '#f59e0b' }}></i>
          <span>Keyword Tips</span>
        </div>
        <div className={styles.tipsContent}>
          <div className={styles.tipItem}>
            <i className="fas fa-check" style={{ color: '#10b981' }}></i>
            <span>Use simple, common words your customers might type</span>
          </div>
          <div className={styles.tipItem}>
            <i className="fas fa-check" style={{ color: '#10b981' }}></i>
            <span>Include variations like "hello", "hi", "start", "info"</span>
          </div>
          <div className={styles.tipItem}>
            <i className="fas fa-star" style={{ color: '#f59e0b' }}></i>
            <span><strong>"any"</strong> keyword = fallback bot (triggers when no specific keywords match)</span>
          </div>
          <div className={styles.tipItem}>
            <i className="fas fa-check" style={{ color: '#10b981' }}></i>
            <span>Keywords are case-insensitive and automatically lowercase</span>
          </div>
          {keywordStats.total > 0 && (
            <div className={styles.tipItem}>
              <i className="fas fa-info-circle" style={{ color: '#3b82f6' }}></i>
              <span>
                Stats: {keywordStats.total} keywords, avg {keywordStats.averageLength} chars each
              </span>
            </div>
          )}
        </div>
      </div>
      
    </div>
  );
};

export default KeywordSection;